import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ProfessionAutocomplete } from '@/components/ui/ProfessionAutocomplete';
import { BusinessTypeAutocomplete } from '@/components/ui/BusinessTypeAutocomplete';
import { useI18n } from '@/contexts/I18nContext';

export interface EditProfessionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { profession: string; businessType: string }) => Promise<void>;
  initialData: { profession: string; businessType: string };
}

export const EditProfessionModal: React.FC<EditProfessionModalProps> = ({ open, onClose, onSave, initialData }) => {
  const { t } = useI18n();
  const [profession, setProfession] = useState(initialData.profession || '');
  const [businessType, setBusinessType] = useState(initialData.businessType || '');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setProfession(initialData.profession || '');
    setBusinessType(initialData.businessType || '');
  }, [initialData, open]);

  // Zera o businessType se a profissão não for Entrepreneur / Business Owner
  useEffect(() => {
    if (profession.trim().toLowerCase() !== t('profile.entrepreneur_business_owner').toLowerCase()) {
      setBusinessType('');
    }
  }, [profession, t]);

  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (!profession.trim()) errs.profession = t('profile.profession_required');
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSaving(true);
    try {
      await onSave({ profession, businessType });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-black font-sans">{t('profile.edit_profession')}</DialogTitle>
          <DialogDescription className="text-gray-600 text-sm">
            {t('profile.edit_profession_description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.profession')}</label>
            <ProfessionAutocomplete value={profession} onChange={setProfession} className="text-black border-black bg-white" />
            {errors.profession && <p className="text-xs text-red-600 mt-1">{errors.profession}</p>}
          </div>
          {profession.trim().toLowerCase() === t('profile.entrepreneur_business_owner').toLowerCase() && (
            <div>
              <label className="block text-sm font-medium mb-1 text-black">{t('profile.business_type')}</label>
              <BusinessTypeAutocomplete value={businessType} onChange={setBusinessType} className="text-black border-black bg-white" />
            </div>
          )}
        </div>
        <Button
          className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-3 h-11 rounded-full transition-colors duration-200 disabled:opacity-50 mt-6"
          onClick={handleSave}
          disabled={saving || Object.keys(validate()).length > 0}
        >
          {saving ? t('profile.saving') : t('common.save')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}; 